import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, Copy, Edit, Trash2, Share2, ExternalLink, History, Clock, Shield, Calendar, Download, AlertTriangle } from 'lucide-react';
import ShareModal from '@/components/ShareModal';
import FileUpload from '@/components/FileUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const calculateExpiryDays = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const getExpiryStatus = (days) => {
  if (days === null) return null;
  if (days < 0) return { label: 'Expired', color: 'text-red-600', bg: 'bg-red-100', icon: AlertTriangle };
  if (days <= 7) return { label: `Expires in ${days} day${days !== 1 ? 's' : ''}`, color: 'text-orange-600', bg: 'bg-orange-100', icon: AlertTriangle };
  if (days <= 30) return { label: `Expires in ${days} days`, color: 'text-yellow-600', bg: 'bg-yellow-100', icon: Calendar };
  return { label: `Expires in ${days} days`, color: 'text-green-600', bg: 'bg-green-100', icon: Calendar };
};

export default function CredentialDetail({ user }) {
  const { id } = useParams();
  const [credential, setCredential] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();

  const fetchHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/credentials/${id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      // History may not be available
    }
  }, [id]);

  const fetchCredential = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/credentials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredential(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Failed to load credential');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchCredential();
    fetchHistory();
  }, [fetchCredential, fetchHistory]);

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this credential?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/credentials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Credential deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete credential');
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/credentials/${id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Credential updated successfully');
      setIsEditing(false);
      fetchCredential();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update credential');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-primary font-manrope text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-6 font-medium"
        data-testid="btn-back-to-credentials"
      >
        <ArrowLeft size={20} />
        Back to Credentials
      </button>

      <div className="max-w-3xl">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight mb-2">
              {credential.title}
            </h1>
            <p className="text-text-secondary">Category: {credential.category}</p>
          </div>
          <div className="flex gap-3">
            {credential.can_edit && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="btn-outline px-4 py-2"
                data-testid="btn-edit-credential"
              >
                <Edit size={18} />
              </button>
            )}
            {(user?.role !== 'viewer' && credential.can_edit) && (
              <button
                onClick={() => setShowShareModal(true)}
                className="btn-outline px-4 py-2"
                data-testid="btn-share-credential"
              >
                <Share2 size={18} />
              </button>
            )}
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="btn-outline px-4 py-2"
              data-testid="btn-view-history"
            >
              <History size={18} />
            </button>
            {credential.can_delete && (
              <button
                onClick={handleDelete}
                className="bg-accent text-white px-4 py-2 hover:bg-accent/90 transition-colors"
                data-testid="btn-delete-credential"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 p-8 space-y-6">
          {isEditing ? (
            <>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Title</label>
                <input
                  type="text"
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="input-edit-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Username</label>
                <input
                  type="text"
                  value={editData.username}
                  onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="input-edit-username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Password</label>
                <input
                  type="text"
                  value={editData.password}
                  onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary font-mono"
                  data-testid="input-edit-password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">URL</label>
                <input
                  type="text"
                  value={editData.url}
                  onChange={(e) => setEditData({ ...editData, url: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="input-edit-url"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Notes</label>
                <textarea
                  value={editData.notes}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  rows={4}
                  className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary resize-none"
                  data-testid="input-edit-notes"
                />
              </div>
              <div className="flex gap-4">
                <button onClick={handleUpdate} className="btn-primary flex-1" data-testid="btn-save-changes">
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditData(credential);
                  }}
                  className="btn-outline flex-1"
                  data-testid="btn-cancel-edit"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-text-muted mb-2">Username</label>
                <div className="flex items-center gap-3">
                  <p className="text-lg text-text-primary flex-1" data-testid="display-username">{credential.username}</p>
                  <button
                    onClick={() => handleCopy(credential.username, 'Username')}
                    className="text-primary hover:text-primary-hover"
                    data-testid="btn-copy-username"
                  >
                    <Copy size={20} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-muted mb-2">Password</label>
                <div className="flex items-center gap-3">
                  <p className="text-lg text-text-primary font-mono flex-1" data-testid="display-password">
                    {showPassword ? credential.password : '•'.repeat(12)}
                  </p>
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-text-muted hover:text-text-primary"
                    data-testid="btn-toggle-password"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                  <button
                    onClick={() => handleCopy(credential.password, 'Password')}
                    className="text-primary hover:text-primary-hover"
                    data-testid="btn-copy-password"
                  >
                    <Copy size={20} />
                  </button>
                </div>
              </div>

              {credential.url && (
                <div>
                  <label className="block text-sm font-medium text-text-muted mb-2">URL</label>
                  <a
                    href={credential.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-2"
                    data-testid="link-credential-url"
                  >
                    {credential.url}
                    <ExternalLink size={16} />
                  </a>
                </div>
              )}

              {credential.notes && (
                <div>
                  <label className="block text-sm font-medium text-text-muted mb-2">Notes</label>
                  <p className="text-text-primary whitespace-pre-wrap" data-testid="display-notes">{credential.notes}</p>
                </div>
              )}

              {/* Certificate Information */}
              {credential.category === 'Certificates' && (
                <div className="pt-6 border-t border-gray-100">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-primary" />
                    <h3 className="font-manrope font-semibold text-lg text-text-primary">Certificate Information</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {credential.certificate_type && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-text-muted mb-1">Type</p>
                        <p className="font-medium text-text-primary">{credential.certificate_type}</p>
                      </div>
                    )}
                    
                    {credential.certificate_provider && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-text-muted mb-1">Provider</p>
                        <p className="font-medium text-text-primary">{credential.certificate_provider}</p>
                      </div>
                    )}
                    
                    {credential.certificate_format && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-text-muted mb-1">Format</p>
                        <p className="font-medium text-text-primary">{credential.certificate_format}</p>
                      </div>
                    )}
                    
                    {credential.expiry_date && (() => {
                      const days = calculateExpiryDays(credential.expiry_date);
                      const status = getExpiryStatus(days);
                      const ExpiryIcon = status?.icon;
                      
                      return (
                        <div className={`rounded-lg p-4 ${status?.bg}`}>
                          <p className="text-xs text-text-muted mb-1">Expiry Date</p>
                          <div className="flex items-center gap-2">
                            {ExpiryIcon && <ExpiryIcon className={`w-4 h-4 ${status.color}`} />}
                            <p className="font-medium text-text-primary">
                              {new Date(credential.expiry_date).toLocaleDateString()}
                            </p>
                          </div>
                          {status && (
                            <p className={`text-xs ${status.color} font-medium mt-1`}>
                              {status.label}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {credential.domain_names && (
                    <div className="mt-4 bg-blue-50 rounded-lg p-4">
                      <p className="text-xs text-text-muted mb-2">Protected Domains</p>
                      <div className="flex flex-wrap gap-2">
                        {credential.domain_names.split(',').map((domain) => (
                          <span
                            key={domain.trim()}
                            className="px-3 py-1 bg-white rounded-full text-sm text-text-primary border border-blue-200"
                          >
                            {domain.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {credential.certificate_details && (
                    <div className="mt-4 bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-text-muted mb-2">Certificate Details</p>
                      <p className="text-sm text-text-primary whitespace-pre-wrap">{credential.certificate_details}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-text-muted">
                  Created: {new Date(credential.created_at).toLocaleString()}
                </p>
                <p className="text-sm text-text-muted">
                  Last updated: {new Date(credential.updated_at).toLocaleString()}
                </p>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <FileUpload
                  credentialId={id}
                  attachments={credential.attachments}
                  onUpdate={fetchCredential}
                  canEdit={credential.can_edit}
                />
              </div>
            </>
          )}
        </div>

        {showHistory && history.length > 0 && (
          <div className="bg-white border-2 border-gray-200 p-6 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={20} className="text-primary" />
              <h3 className="font-manrope font-semibold text-lg text-text-primary">Access & Update History</h3>
            </div>
            <div className="space-y-3">
              {history.map((log) => (
                <div key={log.id || `${log.timestamp}-${log.action}`} className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-text-primary">{log.action.replace(/_/g, ' ').toUpperCase()}</p>
                    <p className="text-xs text-text-secondary">
                      {log.user_name} ({log.user_email}) \u2022 {new Date(log.timestamp).toLocaleString()}
                    </p>
                    {log.changes && Object.keys(log.changes).length > 0 && (
                      <p className="text-xs text-text-muted mt-1">
                        Changes: {JSON.stringify(log.changes, null, 2)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showShareModal && (
        <ShareModal
          resourceId={id}
          resourceType="credential"
          onClose={() => setShowShareModal(false)}
          onSuccess={() => {
            fetchCredential();
            fetchHistory();
          }}
        />
      )}
    </div>
  );
}